'use client';

import { useEffect, useState } from 'react';
import { useBasket } from '~/context/basket-context';

type UseQuantityParams = {
  catalogueRef?: string;
  minimumOrderQuantity?: number;
  // If true, the quantity input will allow the user to set the quantity to 0
  allowZeroQuantity?: boolean;
};

type UseQuantityResponse = {
  // The quantity value as a number
  quantity: number;
  onQuantityChange: (newQuantity: number) => void;
  minQuantity: number;
  isLoading: boolean;
  resetQuantity?: () => void;
};

export function useQuantity({
  catalogueRef,
  allowZeroQuantity,
  minimumOrderQuantity,
}: UseQuantityParams): UseQuantityResponse {
  const [quantity, setQuantity] = useState<number>(minimumOrderQuantity ?? 1);

  const { basket, isInitialLoading } = useBasket();
  const row = basket?.rows.find(
    (row) => row.itemCode.toLowerCase() === catalogueRef?.toLowerCase(),
  );
  const rowQuantity = row?.quantity ?? 0;

  const minQuantity = getMinQuantity(minimumOrderQuantity ?? 1, rowQuantity);

  useEffect(() => {
    // Update the quantity input when the basket loads
    // ! may need to add a check so it runs once
    const min = getMinQuantity(minimumOrderQuantity ?? 1, rowQuantity);
    setQuantity(min);
  }, [minimumOrderQuantity, rowQuantity]);

  function resetQuantity() {
    const min = getMinQuantity(minimumOrderQuantity ?? 1, rowQuantity);
    setQuantity(min);
  }

  function handleQuantityChange(newQuantity: number) {
    const direction = newQuantity > quantity ? 'up' : 'down';

    // unless we are showing the delete icon, we should not allow the quantity to go below MOQ
    if (
      newQuantity < minQuantity &&
      direction === 'down' &&
      !allowZeroQuantity
    ) {
      return;
    }

    if (newQuantity < minQuantity && direction === 'down') {
      setQuantity(0);
      return;
    }

    if (newQuantity < minQuantity) {
      setQuantity(minQuantity);
      return;
    }

    setQuantity(newQuantity);
  }

  return {
    quantity,
    onQuantityChange: handleQuantityChange,
    minQuantity,
    isLoading: isInitialLoading,
    resetQuantity,
  };
}

function getMinQuantity(minimumOrderQuantity = 1, basketQuantity = 0) {
  // If the quantity is less than the minimum quantity, set the quantity to 0
  if (!basketQuantity) {
    return minimumOrderQuantity;
  }

  if (minimumOrderQuantity > basketQuantity) {
    return minimumOrderQuantity - basketQuantity;
  }

  return 1;
}
